/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useTranslation } from "react-i18next";

// components imports
import { Button, Card, Col, Form, Input, Result, Row } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";

import { NAV_LOGIN, AUTH_FORM_LABEL_COL, AUTH_FORM_WRAPPER_COL, NAV_FORGOT_PASSWORD, AUTH_FORM_MAX_WIDTH } from "@/constants";
import { useResetPassword } from "@/api/user";
import { ErrorsComponent } from "@/components/UI/ErrorsComponent";
import { IRequestError } from "@/interfaces/ServerError.interface";
import { validatePassword } from "@/utils/form/validators";

type FieldType = {
  password?: string;
  confirm_password?: string;
};

/**
 * Login Screen
 * @returns JSX.Element
 */
export const ResetPasswordScreen = (): JSX.Element => {
  const { token } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { mutate: resetPasswordMutate, isLoading: isResettingPassword, error, isSuccess } = useResetPassword();

  const onFinish = (values: any) => {
    if (!token) {
      return false;
    }
    resetPasswordMutate({
      token,
      data: { ...values },
    });
  };

  // Validator for Password field
  const handlePasswordValidator = async (_rule: any, value: string) => {
    const errorKey = validatePassword(value);
    const formValues = form.getFieldsValue();
    if (errorKey) {
      return Promise.reject(t(errorKey));
    }

    // Check if the passwords match
    if (
      formValues.password &&
      formValues.password.trim() !== "" &&
      formValues.confirm_password &&
      formValues.confirm_password.trim() !== "" &&
      formValues.password !== formValues.confirm_password
    ) {
      return Promise.reject(t("validation.password_match"));
    }

    return Promise.resolve();
  };

  // If no token exists in the url, just navigate to login form
  if (!token) {
    navigate(NAV_LOGIN);
  }

  // Show form to reset password
  if (!isSuccess) {
    return (
      <Row align={"middle"} justify={"center"} className="w-100">
        <Col lg={12} md={16} xs={24}>
          <Card title={t("forgot_password.title")}>
            <Form
              name="reset-password-form"
              labelCol={{ span: AUTH_FORM_LABEL_COL }}
              labelAlign="left"
              wrapperCol={{ span: AUTH_FORM_WRAPPER_COL }}
              style={{ maxWidth: AUTH_FORM_MAX_WIDTH, margin: "0 auto" }}
              onFinish={onFinish}
              autoComplete="off"
              form={form}
            >
              {/* Password */}
              <Form.Item<FieldType> label={t("common.password")} name="password" rules={[{ required: true, validator: handlePasswordValidator }]}>
                <Input.Password onBlur={() => form.validateFields(["confirm_password"])} />
              </Form.Item>
              {/* ./ Password */}

              {/* Confirm Password */}
              <Form.Item<FieldType>
                label={t("common.confirm_password")}
                name="confirm_password"
                rules={[{ required: true, validator: handlePasswordValidator }]}
              >
                <Input.Password onBlur={() => form.validateFields(["password"])} />
              </Form.Item>
              {/* ./ Confirm Password */}

              {/* Submit button */}
              <Form.Item
                wrapperCol={{
                  offset: AUTH_FORM_LABEL_COL,
                  span: AUTH_FORM_WRAPPER_COL,
                }}
              >
                <Button disabled={isResettingPassword} loading={isResettingPassword} type="primary" htmlType="submit">
                  {t("common.send")}
                </Button>
                <div>
                  <ErrorsComponent error={error as unknown as IRequestError} />
                </div>
              </Form.Item>
              {/* Submit button */}
              {/* Forgot password */}
              <Form.Item
                wrapperCol={{
                  offset: AUTH_FORM_LABEL_COL,
                  span: AUTH_FORM_WRAPPER_COL,
                }}
              >
                <Link to={NAV_LOGIN}>{t("forgot_password.sign_in_link")}</Link>
              </Form.Item>
              {/* ./ Forgot password */}
            </Form>
          </Card>
        </Col>
      </Row>
    );
  }
  return (
    <Row align={"middle"} justify={"center"} className="w-100">
      <Col lg={12} md={16} xs={24}>
        <Card title={t("forgot_password.title")}>
          {isSuccess ? (
            <>
              <Result status="success" title={t("reset_password.success.title")} />
              <Link to={NAV_LOGIN}>{t("forgot_password.sign_in_link")}</Link>
            </>
          ) : (
            <>
              <Result status="error" title={t("reset_password.error.title")} />
              <Link to={NAV_FORGOT_PASSWORD}>{t("common.back")}</Link>
            </>
          )}
        </Card>
      </Col>
    </Row>
  );
};
